/**
 * CMS Service
 *
 * Single entry point for all content fetched from the CDN.
 * All content is fetched from: {CMS_BASE_URL}{path}
 *
 * The CDN returns HTML directory listings which are parsed into:
 *   { files: [{ name: string, url: string, created: null }] }
 *
 * EXPORTED FUNCTIONS:
 *   getWeeklyInsights()                      → weekly market insight PDFs
 *   getMonthlyReports()                      → monthly report PDFs
 *   getStrategyCharts(strategy, period)      → chart images (PNG/JPG/WEBP) for a strategy
 *   getStrategyFactsheets(strategy)          → factsheet PDFs for a strategy
 *   getStrategyDescriptions(strategy)        → strategy description PDFs
 *   getStrategyAllAssets(strategy)           → all docs + charts for a strategy
 *   getBlogs()                               → all blog posts sorted newest first
 *   getBlogBySlug(slug)                      → single blog post by CDN slug
 */

import { CMS_BASE_URL, CMS_PATHS } from './cmsConfig';

// ─────────────────────────────────────────────────────────────────────────────
// CORE FETCHER
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parses a CDN HTML directory listing and returns { files: [] }.
 * The CDN returns a plain HTML page with <a href> links — one per file.
 * Folders (href ending in '/') are skipped automatically.
 *
 * @param {string} html     - Raw HTML from CDN
 * @param {string} folderUrl - Full URL of the folder (used to build file URLs)
 * @returns {{ files: Array<{name: string, url: string, created: null}> }}
 */
const parseCdnHtml = (html, folderUrl) => {
  const base = folderUrl.endsWith('/') ? folderUrl : `${folderUrl}/`;
  const matches = [...html.matchAll(/<a\s+href="([^"]+)">/gi)];
  const files = matches
    .filter(m => !m[1].endsWith('/')) // skip sub-directories
    .map(m => ({
      name: decodeURIComponent(m[1]),
      url: `${base}${m[1]}`,
      created: null
    }));
  return { files };
};

/**
 * Fetches a CDN directory path and returns { files: [] }.
 * @param {string} path - path e.g. '/library/market-insights/weekly'
 * @returns {Promise<{files: Array<{name: string, url: string, created: null}>}>}
 */
const fetchCmsPath = async path => {
  const folderUrl = `${CMS_BASE_URL}${path}`;
  const response = await fetch(folderUrl);

  if (!response.ok) {
    throw new Error(
      `CDN fetch failed for "${path}": ${response.status} ${response.statusText}`
    );
  }

  const html = await response.text();
  return parseCdnHtml(html, folderUrl);
};

/**
 * Fetches a single JSON file directly from the CDN and returns its parsed
 * content. Used for structured data files, not directory listings.
 * @param {string} path - e.g. '/library/market-insights/weekly-insights-content.json'
 * @returns {Promise<Object>}
 */
const fetchCmsJsonFile = async path => {
  const url = `${CMS_BASE_URL}${path}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `CDN fetch failed for "${path}": ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

// Returns the current week's market insight text content.
// Shape: { week, year, label, items: [{ title, description, content, imageUrl }] }
export const getWeeklyInsightContent = async () => {
  const content = await fetchCmsJsonFile(
    CMS_PATHS.MARKET_INSIGHTS_WEEKLY_CONTENT
  );
  const weeklyImageBase = `${CMS_BASE_URL}${CMS_PATHS.MARKET_INSIGHTS_WEEKLY_IMAGES}/`;

  const items = Array.isArray(content?.items)
    ? content.items.map(item => {
        const rawImage = item?.imageUrl || item?.imageFile || null;
        if (!rawImage) return item;

        const imageUrl = /^https?:\/\//i.test(rawImage)
          ? rawImage
          : `${weeklyImageBase}${rawImage.replace(/^\//, '')}`;

        return { ...item, imageUrl };
      })
    : [];

  return { ...content, items };
};

// ─────────────────────────────────────────────────────────────────────────────
// FILENAME PARSERS
// These extract structured metadata from file names so the UI can group,
// sort and label entries without any additional API fields.
// ─────────────────────────────────────────────────────────────────────────────

// Parses a weekly-insight filename.
// Pattern: market-insight_YYYY-W{nn}.pdf
// Returns { year, week, label } or null if the name does not match.
export const parseWeeklyInsightName = name => {
  const match = name.match(/market-insight_(\d{4})-W(\d{1,2})\.pdf$/i);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);
  return {
    year,
    week,
    label: `Week ${String(week).padStart(2, '0')} · ${year}`
  };
};

// Parses a monthly-report filename.
// Pattern: monthly-report_YYYY-MM.pdf
// Returns { year, month, label } or null if the name does not match.
export const parseMonthlyReportName = name => {
  const match = name.match(/monthly-report_(\d{4})-(\d{2})\.pdf$/i);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  return { year, month, label: `${monthName(month)} ${year}` };
};

// Parses a chart filename (both weekly and monthly).
// Weekly pattern:  {strategy}_{type}_YYYY-W{nn}.{ext}   (png|jpg|jpeg|webp)
// Monthly pattern: {strategy}_{type}_YYYY-MM.{ext}      (png|jpg|jpeg|webp)
// Returns { strategy, type, period, year, week?, month?, ext, label } or null.
export const parseChartName = name => {
  // Weekly
  const weekMatch = name.match(
    /^(.+)_(performance|exposure)_(\d{4})-W(\d{1,2})\.(png|jpe?g|webp)$/i
  );
  if (weekMatch) {
    const year = parseInt(weekMatch[3], 10);
    const week = parseInt(weekMatch[4], 10);
    return {
      strategy: weekMatch[1].toLowerCase(),
      type: weekMatch[2].toLowerCase(),
      period: 'week',
      year,
      week,
      ext: weekMatch[5].toLowerCase(),
      label: `Week ${String(week).padStart(2, '0')} · ${year}`
    };
  }

  // Monthly
  const monthMatch = name.match(
    /^(.+)_(performance|exposure)_(\d{4})-(\d{2})\.(png|jpe?g|webp)$/i
  );
  if (monthMatch) {
    const year = parseInt(monthMatch[3], 10);
    const month = parseInt(monthMatch[4], 10);
    return {
      strategy: monthMatch[1].toLowerCase(),
      type: monthMatch[2].toLowerCase(),
      period: 'month',
      year,
      month,
      ext: monthMatch[5].toLowerCase(),
      label: `${monthName(month)} ${year}`
    };
  }

  return null;
};

// Parses a factsheet or description filename.
// Pattern: {strategy}_{factsheet|description}_YYYY-MM.pdf
// Returns { strategy, docType, year, month, label } or null.
export const parseDocumentName = name => {
  const match = name.match(
    /^(.+)_(factsheet|description)_(\d{4})-(\d{2})\.pdf$/i
  );
  if (!match) return null;
  const year = parseInt(match[3], 10);
  const month = parseInt(match[4], 10);
  return {
    strategy: match[1].toLowerCase(),
    docType: match[2].toLowerCase(),
    year,
    month,
    label: `${monthName(month)} ${year}`
  };
};

// Parses a description cover filename.
// Pattern: {strategy}_description-cover_YYYY-MM.jpg (or .png/.jpeg/.webp)
// Returns { strategy, year, month, ext, label } or null.
export const parseDescriptionCoverName = name => {
  const match = name.match(
    /^(.+)_description-cover_(\d{4})-(\d{2})\.(jpg|jpeg|png|webp)$/i
  );
  if (!match) return null;
  const year = parseInt(match[2], 10);
  const month = parseInt(match[3], 10);
  return {
    strategy: match[1].toLowerCase(),
    year,
    month,
    ext: match[4].toLowerCase(),
    label: `${monthName(month)} ${year}`
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

// Returns all weekly market insights, sorted newest first.
// Each entry: { name, url, created, year, week, label }
export const getWeeklyInsights = async () => {
  const { files } = await fetchCmsPath(CMS_PATHS.MARKET_INSIGHTS_WEEKLY);
  return files
    .map(f => ({ ...f, ...parseWeeklyInsightName(f.name) }))
    .filter(f => f.year) // remove unparseable entries
    .sort((a, b) => b.year - a.year || b.week - a.week);
};

// Returns all monthly reports, sorted newest first.
// Each entry: { name, url, created, year, month, label }
export const getMonthlyReports = async () => {
  const { files } = await fetchCmsPath(CMS_PATHS.MARKET_INSIGHTS_MONTHLY);
  return files
    .map(f => ({ ...f, ...parseMonthlyReportName(f.name) }))
    .filter(f => f.year)
    .sort((a, b) => b.year - a.year || b.month - a.month);
};

// Returns all charts for a strategy and period, sorted newest first.
// strategy: CMS slug e.g. 'strand-q'. period: 'week' or 'month'.
// Each entry includes parsed metadata (type, year, week/month) plus url.
export const getStrategyCharts = async (strategy, period) => {
  const path =
    period === 'week'
      ? CMS_PATHS.STRATEGY_CHARTS_WEEKLY(strategy)
      : CMS_PATHS.STRATEGY_CHARTS_MONTHLY(strategy);

  const { files } = await fetchCmsPath(path);
  return files
    .map(f => ({ ...f, ...parseChartName(f.name) }))
    .filter(f => f.year)
    .sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return period === 'week' ? b.week - a.week : b.month - a.month;
    });
};

// Returns all factsheets for a strategy, sorted newest first.
// Each entry: { name, url, created, year, month, label }
// Strategy slug examples: 'strand-q', 'merin', 'dravon'
export const getStrategyFactsheets = async strategy => {
  const { files } = await fetchCmsPath(CMS_PATHS.STRATEGY_FACTSHEETS(strategy));
  return files
    .map(f => ({ ...f, ...parseDocumentName(f.name) }))
    .filter(f => f.year)
    .sort((a, b) => b.year - a.year || b.month - a.month);
};

// Returns strategy description documents and latest cover for a strategy.
// descriptions: sorted newest first — [{ name, url, created, year, month, label }]
// cover: latest cover image entry or null
export const getStrategyDescriptions = async strategy => {
  const { files } = await fetchCmsPath(
    CMS_PATHS.STRATEGY_DESCRIPTIONS(strategy)
  );

  const descriptions = files
    .map(f => ({ ...f, ...parseDocumentName(f.name) }))
    .filter(f => f.year)
    .sort((a, b) => b.year - a.year || b.month - a.month);

  const covers = files
    .map(f => ({ ...f, ...parseDescriptionCoverName(f.name) }))
    .filter(f => f.year)
    .sort((a, b) => b.year - a.year || b.month - a.month);

  return { descriptions, cover: covers[0] ?? null };
};

// Returns just the latest cover image for a strategy description.
// Lightweight version of getStrategyDescriptions when you only need the cover.
export const getStrategyDescriptionCover = async strategy => {
  const { cover } = await getStrategyDescriptions(strategy);
  return cover;
};

// Fetches all assets for a strategy in parallel.
// Returns { weeklyCharts, monthlyCharts, factsheets, descriptions, descriptionCover }.
// Strategy slug examples: 'strand-q', 'merin', 'dravon'
export const getStrategyAllAssets = async strategy => {
  const [weeklyCharts, monthlyCharts, factsheets, { descriptions, cover }] =
    await Promise.all([
      getStrategyCharts(strategy, 'week'),
      getStrategyCharts(strategy, 'month'),
      getStrategyFactsheets(strategy),
      getStrategyDescriptions(strategy)
    ]);

  return {
    weeklyCharts,
    monthlyCharts,
    factsheets,
    descriptions,
    descriptionCover: cover
  };
};

// Returns performance data for a strategy from CDN JSON file.
// Strategy slug examples: 'strand-q', 'merin', 'dravon'
// Returns: { lastUpdated, hero: { tagline }, performanceStats: [{ label, value }] }
export const getStrategyPerformance = async strategy => {
  const data = await fetchCmsJsonFile(CMS_PATHS.STRATEGY_PERFORMANCE(strategy));
  return data;
};

// Returns benchmark comparison data for a strategy from CDN JSON file.
// Strategy slug examples: 'strand-q', 'merin', 'dravon'
export const getStrategyBenchmarks = async strategy => {
  const data = await fetchCmsJsonFile(CMS_PATHS.STRATEGY_BENCHMARKS(strategy));
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// BLOG
// ─────────────────────────────────────────────────────────────────────────────

// Supported cover image extensions (checked against the CDN /blogs/ listing).
const BLOG_COVER_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];

// Given the folder listing files and a blog slug, returns the actual cover
// filename if it exists (any supported extension), else falls back to .png.
const getBlogCoverFileName = (slug, files) => {
  const lower = slug.toLowerCase();
  const match = (files || []).find(name => {
    const dot = name.toLowerCase().lastIndexOf('.');
    if (dot <= 0) return false;
    const stem = name.slice(0, dot).toLowerCase();
    const ext = name.slice(dot + 1).toLowerCase();
    return stem === lower && BLOG_COVER_EXTENSIONS.includes(ext);
  });
  return match || `${slug}.png`;
};

// Injects resolved CDN URLs into a blog data object.
// coverImage → {CMS_BASE_URL}/blogs/{slug}.{png|jpg|jpeg|webp}
// pdfLink    → {CMS_BASE_URL}/blogs/{slug}.pdf only when data.hasPdf === true
const resolveBlogAssets = (slug, data, coverFile) => {
  const base = `${CMS_BASE_URL}${CMS_PATHS.BLOGS}`;
  return {
    ...data,
    coverImage: `${base}/${coverFile || `${slug}.png`}`,
    pdfLink: data.hasPdf ? `${base}/${slug}.pdf` : null
  };
};

// Returns all blog posts, sorted newest first.
// Reads the /blogs/ directory listing, then fetches each .json in parallel.
// Each entry: { id, title, subtitle, date, categories, readTime, excerpt,
//               hasPdf, sections, coverImage, pdfLink }
export const getBlogs = async () => {
  const { files } = await fetchCmsPath(CMS_PATHS.BLOGS);
  const jsonFiles = files.filter(f => f.name.toLowerCase().endsWith('.json'));

  const blogs = await Promise.all(
    jsonFiles.map(async f => {
      const slug = f.name.replace(/\.json$/i, '');
      const data = await fetchCmsJsonFile(`${CMS_PATHS.BLOGS}/${f.name}`);
      return resolveBlogAssets(
        slug,
        data,
        getBlogCoverFileName(slug, files.map(x => x.name))
      );
    })
  );

  return blogs
    .filter(b => b.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Returns a single blog post by its CDN slug.
// Slug matches the filename without extension, e.g. 'eur-not-currency'
export const getBlogBySlug = async slug => {
  const data = await fetchCmsJsonFile(`${CMS_PATHS.BLOGS}/${slug}.json`);

  // Resolve the actual cover image extension from the /blogs/ listing.
  let coverFile = null;
  try {
    const { files } = await fetchCmsPath(CMS_PATHS.BLOGS);
    coverFile = getBlogCoverFileName(slug, files.map(x => x.name));
  } catch {
    coverFile = `${slug}.png`;
  }

  return resolveBlogAssets(slug, data, coverFile);
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// Returns full English month name for a 1-based month integer (1 = January).
export const monthName = m => MONTH_NAMES[m] || '';
